import { useEffect, useState, useRef } from 'react';
import { Folder, MoreVertical, Circle, Trash2 } from 'lucide-react';
import Storico from '../../pages/documenti/Storico';
import { conteggioCartella } from '../../pages/documenti/archivioBlocchi';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

var COLORI_TIPO = {
  clienti: { icon: 'var(--zone-amber)' }, cantiere: { icon: 'var(--zone-blue)' },
  fornitori: { icon: 'var(--zone-violet)' }, commercialista: { icon: 'var(--ok)' },
  generale: { icon: 'var(--ds-muted)' }, libera: { icon: 'var(--ds-muted)' },
  partner: { icon: 'var(--zone-amber)' },
};
var COLORE_PALETTE = [
  { name: 'amber', hex: '#FFB300' }, { name: 'green', hex: 'var(--brand)' },
  { name: 'blue', hex: '#4458A8' }, { name: 'purple', hex: '#8E24AA' },
  { name: 'red', hex: '#E53E3E' }, { name: 'gray', hex: '#9E9E9E' },
  { name: 'teal', hex: '#0c8599' }, { name: 'coral', hex: '#FF6B6B' },
];

var styles = {
  container: { padding: '16px' },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px', fontSize: '12px', color: 'var(--ds-muted)' },
  breadcrumbLink: { cursor: 'pointer', color: 'var(--brand)', background: 'none', border: 'none', padding: 0, fontSize: '12px', fontWeight: 500 },
  breadcrumbSep: { margin: '0 2px', color: 'var(--ds-muted)' },
  breadcrumbCurrent: { fontWeight: 500, color: 'var(--ink)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' },
  sectionTitle: { fontSize: '11px', fontWeight: 500, marginBottom: '12px', color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  empty: { textAlign: 'center', padding: '40px 16px', color: 'var(--ds-muted)', fontSize: '13px' },
  // Card stile Assistente AI
  card: {
    borderRadius: 'var(--radius-card)', padding: '16px', cursor: 'pointer',
    border: '1px dashed var(--brand)', background: 'var(--ds-bg)',
    transition: 'box-shadow 150ms', position: 'relative',
    display: 'flex', flexDirection: 'column', gap: '6px',
    minHeight: '100px', overflow: 'visible',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '10px' },
  cardName: { fontSize: '13px', fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' },
  cardCount: { fontSize: '11px', color: 'var(--ds-muted)' },
  // Menu dropdown
  menuBtn: {
    position: 'absolute', top: '4px', right: '4px',
    width: '44px', height: '44px', borderRadius: 'var(--radius-base)',
    border: 'none', background: 'transparent',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--ds-muted)', padding: 0,
    transition: 'background 150ms',
  },
  dropdown: {
    position: 'absolute', top: '38px', right: '0px',
    background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
    borderRadius: 'var(--radius-base)', boxShadow: CARD_SHADOW,
    zIndex: 100, minWidth: '170px', padding: '4px 0',
    animation: 'fadeIn 150ms ease-out',
  },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 12px', fontSize: '12px', cursor: 'pointer',
    background: 'none', border: 'none', width: '100%', textAlign: 'left',
    color: 'var(--ds-text)',
  },
  colorGrid: { display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 12px' },
  colorDot: { width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', border: '2px solid transparent', transition: 'border-color 150ms', backgroundClip: 'content-box', padding: '7px' },
};

// -- Icons --
function FolderIconColored(props) {
  var color = props.color || 'var(--brand)';
  return (<Folder size={24} color={color} strokeWidth={2} />);
}

// -- FolderCard stile Assistente AI --
function FolderCard(props) {
  var c = props.cartella; var onNavigaCartella = props.onNavigaCartella;
  var onDeleteCartella = props.onDeleteCartella; var onColorChange = props.onColorChange;
  var [hovered, setHovered] = useState(false);
  var [menuOpen, setMenuOpen] = useState(false);
  var [showColors, setShowColors] = useState(false);
  var menuRef = useRef(null);

  var customColor = c.colore ? COLORE_PALETTE.find(function(p) { return p.name === c.colore; }) : null;
  var iconColor = customColor ? customColor.hex : ((COLORI_TIPO[c.tipo] || COLORI_TIPO.generale).icon);

  // FE-088 — il numero del server comprende gia' le sottocartelle: non si risomma.
  var totalDocs = conteggioCartella(c);
  var countLabel = totalDocs + ' document' + (totalDocs === 1 ? 'o' : 'i') + ' totali';
  if (c.sottocartelle && c.sottocartelle.length > 0) {
    countLabel = countLabel + ', ' + c.sottocartelle.length + ' cartelle';
  }

  useEffect(function() {
    if (!menuOpen) return;
    var handler = function(e) { if (menuRef.current && !menuRef.current.contains(e.target)) { setMenuOpen(false); setShowColors(false); } };
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, [menuOpen]);

  return (
    <div style={styles.card}
      onClick={function() { if (!menuOpen) onNavigaCartella(c.id); }}
      onMouseEnter={function(e) { setHovered(true); e.currentTarget.style.boxShadow = CARD_SHADOW; }}
      onMouseLeave={function(e) { setHovered(false); e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Header: icona + nome */}
      <div style={styles.cardHeader}>
        <FolderIconColored color={iconColor} />
        <div>
          <div style={styles.cardName}>{c.nome}</div>
          <div style={styles.cardCount}>{countLabel}</div>
        </div>
      </div>

      {/* Bottone 3 punti */}
      {(hovered || menuOpen) && (
        <button
          style={Object.assign({}, styles.menuBtn, { background: hovered ? 'var(--chip)' : 'transparent' })}
          onClick={function(e) { e.stopPropagation(); setMenuOpen(!menuOpen); setShowColors(false); }}
          onMouseDown={function(e) { e.currentTarget.style.background = 'var(--chip)'; }}
          onMouseUp={function(e) { e.currentTarget.style.background = 'transparent'; }}
        >
          <MoreVertical size={14} strokeWidth={2} />
        </button>
      )}

      {/* Dropdown menu — posizionato fuori dalla card */}
      {menuOpen && (
        <div ref={menuRef} style={styles.dropdown} onClick={function(e) { e.stopPropagation(); }}>
          {!showColors ? (
            <div>
              <button style={styles.dropdownItem}
                onMouseEnter={function(e) { e.currentTarget.style.background = 'var(--chip)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = 'none'; }}
                onClick={function() { setShowColors(true); }}
              >
                <Circle size={14} strokeWidth={2} />
                Colore cartella
              </button>
              {!c.is_predefinita && (
                <button style={Object.assign({}, styles.dropdownItem, { color: 'var(--ds-danger)' })}
                  onMouseEnter={function(e) { e.currentTarget.style.background = tint('var(--ds-danger)', 8); }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = 'none'; }}
                  onClick={function() { setMenuOpen(false); if (onDeleteCartella) onDeleteCartella(c.id); }}
                >
                  <Trash2 size={14} strokeWidth={2} />
                  Elimina
                </button>
              )}
            </div>
          ) : (
            <div>
              <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 500, color: 'var(--ds-muted)' }}>Scegli colore</div>
              <div style={styles.colorGrid}>
                {COLORE_PALETTE.map(function(col) {
                  var isSelected = c.colore === col.name;
                  return (<div key={col.name} style={Object.assign({}, styles.colorDot, { background: col.hex, borderColor: isSelected ? 'var(--ink)' : 'transparent' })}
                    onClick={function() { setMenuOpen(false); setShowColors(false); if (onColorChange) onColorChange(c.id, col.name); }}
                    onMouseEnter={function(e) { e.currentTarget.style.borderColor = 'var(--ds-muted)'; }}
                    onMouseLeave={function(e) { if (!isSelected) e.currentTarget.style.borderColor = 'transparent'; }}
                  />);
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -- Main --
export default function VistaGriglia(props) {
  var vista = props.vista; var cartelle = props.cartelle || []; var cartellaAttiva = props.cartellaAttiva; var refreshKey = props.refreshKey;
  var onNavigaCartella = props.onNavigaCartella;
  var onDeleteCartella = props.onDeleteCartella; var onColorChange = props.onColorChange;
  var showToast = props.showToast; var onUpload = props.onUpload;
  var onTogglePreferito = props.onTogglePreferito; var preferitiLocali = props.preferitiLocali;
  var [breadcrumb, setBreadcrumb] = useState([]);

  useEffect(function() { if (!cartellaAttiva) { setBreadcrumb([]); return; } var path = []; if (_cercaCartella(cartelle, cartellaAttiva, path)) setBreadcrumb(path); }, [cartellaAttiva, cartelle]);

  // Vista Archivio
  if (vista === 'archivio') {
    var sottoCartelle = [];
    if (!cartellaAttiva) sottoCartelle = cartelle;
    else { var cart = _trovaCartella(cartelle, cartellaAttiva); sottoCartelle = cart ? (cart.sottocartelle || []) : []; }

    return (
      <div style={styles.container}>
        {/* Banner intro — sfondo bianco, stile Assistente AI */}
        {!cartellaAttiva && (
          <div style={{ padding: '16px 0', marginBottom: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink)', marginBottom: '4px', fontFamily: 'var(--font-display)' }}>Il tuo archivio documenti</div>
            <div style={{ fontSize: '12px', color: 'var(--ds-muted)' }}>Organizza contratti, fatture, foto e documenti per cliente, cantiere e fornitore</div>
          </div>
        )}

        {/* Breadcrumb */}
        {breadcrumb.length > 0 && (
          <div style={styles.breadcrumb}>
            <button style={styles.breadcrumbLink} onClick={function() { onNavigaCartella(null); }}>Archivio</button>
            {breadcrumb.map(function(item, i) {
              var isLast = i === breadcrumb.length - 1;
              return (
                <span key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={styles.breadcrumbSep}>/</span>
                  {isLast ? <span style={styles.breadcrumbCurrent}>{item.nome}</span> : <button style={styles.breadcrumbLink} onClick={function() { onNavigaCartella(item.id); }}>{item.nome}</button>}
                </span>
              );
            })}
          </div>
        )}

        {/* Cartelle — card stile Assistente AI */}
        {sottoCartelle.length > 0 && (
          <div>
            <div style={styles.sectionTitle}>Cartelle</div>
            <div style={styles.grid}>
              {sottoCartelle.map(function(c) {
                return <FolderCard key={c.id} cartella={c} onNavigaCartella={onNavigaCartella} onDeleteCartella={onDeleteCartella} onColorChange={onColorChange} />;
              })}
            </div>
          </div>
        )}

        {/* Documenti nella cartella — riusa Storico */}
        {cartellaAttiva && (
          <div style={{ marginTop: sottoCartelle.length > 0 ? '24px' : '0' }}>
            <Storico
              key={refreshKey}
              showToast={showToast || function() {}}
              onUpload={onUpload || function() {}}
              onTogglePreferito={onTogglePreferito}
              preferitiLocali={preferitiLocali}
              extraParams={{ cartella: cartellaAttiva }}
            />
          </div>
        )}

        {/* Ultimi documenti caricati — visibile solo in root archivio */}
        {!cartellaAttiva && (
          <div style={{ marginTop: '28px' }}>
            <div style={styles.sectionTitle}>Ultimi documenti caricati</div>
            <Storico
              key={'recent-' + refreshKey}
              showToast={showToast || function() {}}
              onUpload={onUpload || function() {}}
              onTogglePreferito={onTogglePreferito}
              preferitiLocali={preferitiLocali}
              extraParams={{ ordering: '-data_upload' }}
              limite={5}
              compatto={false}
            />
          </div>
        )}

        {!cartellaAttiva && sottoCartelle.length === 0 && <div style={styles.empty}>Nessuna cartella disponibile</div>}
      </div>
    );
  }

  // Recenti / Preferiti / Condivisi — riusa Storico con extraParams
  var extraParams = {};
  if (vista === 'recenti') extraParams = { ordering: '-data_upload' };
  else if (vista === 'preferiti') extraParams = { preferito: 'true' };
  else if (vista === 'condivisi') extraParams = { condivisi: 'true' };

  return (
    <div style={styles.container}>
      <Storico
        key={'vista-' + refreshKey}
        showToast={showToast || function() {}}
        onUpload={onUpload || function() {}}
              onTogglePreferito={onTogglePreferito}
              preferitiLocali={preferitiLocali}
        extraParams={extraParams}
      />
    </div>
  );
}

function _cercaCartella(cartelle, targetId, path) { for (var i = 0; i < cartelle.length; i++) { var c = cartelle[i]; path.push({ id: c.id, nome: c.nome }); if (c.id === targetId) return true; if (c.sottocartelle && c.sottocartelle.length > 0) { if (_cercaCartella(c.sottocartelle, targetId, path)) return true; } path.pop(); } return false; }
function _trovaCartella(cartelle, targetId) { for (var i = 0; i < cartelle.length; i++) { if (cartelle[i].id === targetId) return cartelle[i]; if (cartelle[i].sottocartelle) { var f = _trovaCartella(cartelle[i].sottocartelle, targetId); if (f) return f; } } return null; }
